import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectEssSwitch } from 'shared/slice/essSwitchSlice';
import { selectTesSwitch } from 'shared/slice/tesSwitchSlice';
import { selectTgsSwitch } from 'shared/slice/tgsSwitchSlice';
import {
  flexBoxCenter,
  layerA,
  layerA180Deg,
  layerC,
} from 'shared/styles/commonStyles';
import BarGraph from './BarGraph';
import LineGraph from './LineGraph';
import ChartViewport from './chartControls/ChartViewport';
import ChartScaleControls from './chartControls/ChartScaleControls';
import ChartDetailControls from './chartControls/ChartDetailControls';
import { useChartScale } from './chartControls/useChartScale';
import { showDetailControl } from './chartControls/chartControlVisibility';

const ChartContainer = ({
  isReadyToRender,
  chartOption,
  swtName,
  graph,
  location,
  machine,
  onPointClick,
  canDrillOut,
  onDrillOut,
}) => {
  const { flatEssSwitch,flatTgsSwitch,flatTesSwitch } = useSelector(
    swtName === 'ess'
      ? selectEssSwitch
      : swtName === 'tgs'
        ? selectTgsSwitch
        : selectTesSwitch
  );
  const swtStatus =
    swtName === 'ess' ? flatEssSwitch : swtName === 'tgs' ? flatTgsSwitch : flatTesSwitch;
  const { isWifi } = swtStatus[location][machine];

  const { scale, scaleIn, scaleOut, reset, canScaleIn, canScaleOut } =
    useChartScale();
  const withDetail = showDetailControl(isReadyToRender, chartOption);

  // A zoom level chosen for one series is meaningless on the next, so the
  // scale drops back to 1x whenever the selected graph changes. Keyed on the
  // index of the active option rather than the array, which is a new
  // identity on every render.
  const activeOption = chartOption?.findIndex(Boolean);
  useEffect(() => {
    reset();
  }, [activeOption, reset]);

  // One definition of "which chart is on screen", rendered twice: once
  // scrolling, and once as the pinned axis-only copy. Building both from the
  // same expression keeps their y domains identical, which is what makes the
  // pinned axis line up with the plot beside it.
  const renderChart = (extra) =>
    isReadyToRender ? (
      chartOption[1] ? (
        <LineGraph
          swtName={swtName}
          location={location}
          machine={machine}
          realTime={false}
          onPointClick={onPointClick}
          {...extra}
        />
      ) : chartOption[0] ? (
        isWifi ? (
          <LineGraph
            swtName={swtName}
            location={location}
            machine={machine}
            realTime={true}
            {...extra}
          />
        ) : (
          <EmptyGraph src='/images/empty-graph.svg' />
        )
      ) : (
        (chartOption[2] || chartOption[3]) && (
          <BarGraph
            swtName={swtName}
            graph={graph}
            location={location}
            machine={machine}
            onPointClick={chartOption[2] ? onPointClick : undefined}
            {...extra}
          />
        )
      )
    ) : isWifi ? (
      <LineGraph
        swtName={swtName}
        location={location}
        machine={machine}
        realTime={true}
        {...extra}
      />
    ) : (
      <EmptyGraph src='/images/empty-graph.svg' />
    );

  // Only worth pinning an axis once the chart is wide enough to scroll, and
  // only when there is a chart rather than the empty-graph placeholder.
  const hasChart = isReadyToRender
    ? Boolean(chartOption[1] || chartOption[2] || chartOption[3])
    : isWifi;
  const isScaled = scale > 1;

  return (
    <Wrapper>
      <InnerWrapper>
        <TopWrapper>
          <Overlay>
            <ChartScaleControls
              scale={scale}
              canScaleIn={canScaleIn}
              canScaleOut={canScaleOut}
              onScaleIn={scaleIn}
              onScaleOut={scaleOut}
            />
            {withDetail && (
              <ChartDetailControls
                canDrillOut={canDrillOut}
                onDrillOut={onDrillOut}
              />
            )}
          </Overlay>

          <ChartViewport
            scale={scale}
            axis={
              isScaled && hasChart ? renderChart({ axisOnly: true }) : null
            }
          >
            {renderChart({ hideYAxis: isScaled && hasChart })}
          </ChartViewport>
        </TopWrapper>
      </InnerWrapper>
    </Wrapper>
  );
};

export default ChartContainer;

const Wrapper = styled.div`
  width: 743px;
  height: 322px;
  border-radius: 14px;

  ${layerC};
  ${flexBoxCenter};
`;
const InnerWrapper = styled.div`
  width: 739px;
  height: 318px;
  border-radius: 12px;

  ${layerA180Deg};
  ${flexBoxCenter};
`;
const TopWrapper = styled.div`
  width: 730px;
  height: 308px;
  border-radius: 8px;

  ${layerA};
  ${flexBoxCenter}
  padding-left: 10px;
  padding-top: 20px;

  position: relative;
`;

const Overlay = styled.div`
  position: absolute;
  top: 4px;
  right: 10px;
  z-index: 5;

  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const EmptyGraph = styled.img``;
